import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";
import Footer from "../../components/Footer";

export default function TeamPayPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [team, setTeam] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [amount, setAmount] = useState("");
    const [payFees, setPayFees] = useState(true);
    const [currency, setCurrency] = useState('eur');
    const [currencySymbol, setCurrencySymbol] = useState('€');
    const [paymentError, setPaymentError] = useState(null);
    const [paymentLoading, setPaymentLoading] = useState(false);

    useEffect(() => {
        fetch(`${process.env.REACT_APP_API_URL}/Equipe/${id}`)
            .then(async (res) => {
                if (!res.ok) throw new Error("Erreur réseau");
                return res.json();
            })
            .then((data) => {
                if (
                    data.liste_membre.length === 1 &&
                    data.liste_membre[0].Prenom === "Equipe inexistante"
                ) {
                    throw new Error("Équipe introuvable");
                }

                const teamEntry = data.liste_membre.find(
                    (m) => m.Prenom === "Faire dons a toute l'equipe"
                );

                setTeam({
                    nom: teamEntry ? teamEntry.Nom : "Équipe",
                    id: id,
                });

                if (data.currency) setCurrency(data.currency.toLowerCase());
                if (data.currencySymbol) setCurrencySymbol(data.currencySymbol);

                setLoading(false);
            })
            .catch(() => {
                setError("Impossible de charger l'équipe.");
                setLoading(false);
            });
    }, [id]);

    // -----------------------------------
    // Fonction de calcul des frais
    // -----------------------------------
    const handleAmountChange = (e) => {
        const val = e.target.value;
        if (val < 0) return;
        setAmount(val);
    };

    const handlePresetClick = (value) => setAmount(value.toString());

    const calculateDisplayValues = () => {
        const val = parseFloat(amount);
        if (!val || val <= 0) return { fees: "0.00", total: "0.00", netAmount: "0.00" };

        const G = Math.round(val * 100);
        const commission = Math.max(Math.round(G * 0.05), 15);

        if (payFees) {
            const T = Math.ceil((G + commission + 25) / (1 - 0.015));
            const total = (T / 100).toFixed(2);
            const fees = ((T - G) / 100).toFixed(2);
            return { fees, total, netAmount: val.toFixed(2) };
        }

        const stripeEstime = Math.ceil(G * 0.02) + 25;
        const totalFees = commission + stripeEstime;
        const net = G - totalFees;
        return { fees: "0.00", total: val.toFixed(2), netAmount: (net / 100).toFixed(2) };
    };

    const { fees, total, netAmount } = calculateDisplayValues();

    // -----------------------------------
    // Paiement
    // -----------------------------------
    const handlePay = async () => {
        if (!amount || parseFloat(amount) <= 0) return;
        setPaymentError(null);
        setPaymentLoading(true);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/paiement/create_link_team`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    amount: Math.round(parseFloat(total) * 100),
                    netAmount: Math.round(parseFloat(netAmount) * 100),
                    id_equipe: id,
                    currency: currency
                }),
            });

            if (!response.ok) {
                throw new Error('Erreur lors de la création du lien de paiement');
            }

            const data = await response.json();
            if (data.url) {
                window.location.href = data.url;
            } else {
                setPaymentError("Lien de paiement introuvable. Veuillez réessayer.");
            }

        } catch {
            setPaymentError("Une erreur est survenue. Veuillez réessayer.");
        } finally {
            setPaymentLoading(false);
        }
    };

    // -----------------------------------
    // États UI
    // -----------------------------------
    if (loading)
        return (
            <div className="min-h-screen bg-modern flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
            </div>
        );

    if (error)
        return (
            <div className="min-h-screen bg-modern flex items-center justify-center p-4">
                <div className="bg-white p-8 rounded-2xl shadow-xl text-center">
                    <h2 className="text-2xl font-bold mb-2 text-red-500">Erreur</h2>
                    <p>{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="mt-4 bg-black text-white px-4 py-2 rounded-lg"
                    >
                        Réessayer
                    </button>
                </div>
            </div>
        );

    // -----------------------------------
    // RENDER PRINCIPAL
    // -----------------------------------
    return (
        <div className="relative no-scroll min-h-screen bg-modern text-black flex flex-col justify-between overflow-hidden">
            {/* Bouton retour vers l'équipe */}
            <button
                onClick={() => navigate(`/team/${id}/members`)}
                className="absolute top-4 left-4 z-20 bg-black text-white p-3 rounded-full shadow-lg transition hover:scale-105"
            >
                <IoArrowBack size={20} />
            </button>

            <div className="container mx-auto w-full flex flex-col items-center h-screen pt-10 px-4 overflow-y-auto">

                {/* ----------- INFOS ÉQUIPE ----------- */}
                <div className="flex flex-col items-center mb-8 mt-10">
                    <div className="w-32 h-32 rounded-full mb-4 bg-gray-300 flex items-center justify-center shadow-lg border-4 border-white">
                        <span className="text-4xl text-gray-600">
                            {team.nom?.charAt(0) ?? "?"}
                        </span>
                    </div>

                    <h2 className="text-3xl font-bold text-center capitalize">
                        {team.nom}
                    </h2>
                </div>

                {/* -------- INPUT MONTANT -------- */}
                <div className="w-full max-w-xs mb-6 relative">
                    <input
                        type="number"
                        min="0"
                        inputMode="decimal"
                        value={amount}
                        onChange={handleAmountChange}
                        placeholder="0"
                        className="w-full p-4 text-center text-4xl font-bold bg-white/50 backdrop-blur-sm border-2 border-transparent focus:border-black rounded-2xl shadow-inner outline-none transition"
                    />
                    <span className="absolute right-8 top-1/2 transform -translate-y-1/2 text-2xl font-bold text-gray-500">{currencySymbol}</span>
                </div>

                {/* -------- PRESETS -------- */}
                <div className="flex justify-center space-x-4 mb-8 w-full max-w-md">
                    {[3, 5, 10].map((val) => (
                        <button
                            key={val}
                            onClick={() => handlePresetClick(val)}
                            className={`flex-1 py-3 rounded-xl font-bold text-lg transition ${amount === val.toString()
                                ? "bg-black text-white"
                                : "bg-white text-black"
                                }`}
                        >
                            {val}{currencySymbol}
                        </button>
                    ))}
                </div>

                {/* -------- CHECKBOX FRAIS -------- */}
                <div className="flex items-center mb-8 bg-white/30 p-4 rounded-xl backdrop-blur-sm">
                    <input
                        type="checkbox"
                        id="fees"
                        checked={payFees}
                        onChange={(e) => setPayFees(e.target.checked)}
                        className="w-6 h-6 mr-3 accent-black"
                    />
                    <label htmlFor="fees" className="text-lg font-medium">
                        Je paie les frais
                    </label>
                </div>

                {/* -------- RÉCAP -------- */}
                {amount > 0 && (
                    <div className="w-full max-w-xs bg-white/80 backdrop-blur-md rounded-2xl shadow-xl p-6 mb-8">
                        <div className="flex justify-between text-gray-600">
                            <span>Montant</span>
                            <span className="font-bold">{parseFloat(amount).toFixed(2)}{currencySymbol}</span>
                        </div>

                        {payFees ? (
                            <div className="flex justify-between text-gray-500">
                                <span>Frais</span>
                                <span>{fees}{currencySymbol}</span>
                            </div>
                        ) : (
                            <div className="flex justify-between text-gray-500 text-sm">
                                <span>{team.nom} reçoit</span>
                                <span className="font-semibold">{netAmount}{currencySymbol}</span>
                            </div>
                        )}

                        <div className="flex justify-between text-2xl font-bold border-t pt-2 mt-2">
                            <span>Total</span>
                            <span>{total}{currencySymbol}</span>
                        </div>
                    </div>
                )}

                {/* -------- BOUTON PAYER -------- */}
                <button
                    onClick={handlePay}
                    disabled={paymentLoading}
                    className="w-full max-w-xs px-6 py-4 bg-black text-white font-bold rounded-xl shadow-lg text-xl hover:bg-gray-900 transition mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {paymentLoading ? (
                        <span className="flex items-center justify-center gap-2">
                            <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Chargement…
                        </span>
                    ) : (
                        <>Payer {amount > 0 ? `${total}${currencySymbol}` : ""}</>
                    )}
                </button>

                {paymentError && (
                    <div className="w-full max-w-xs mb-10 bg-red-50 border border-red-200 text-red-600 text-sm text-center px-4 py-3 rounded-xl">
                        {paymentError}
                    </div>
                )}
            </div>

            <Footer />
        </div>
    );
}
